import { Card } from "@/components/ui/card";
import { AlertCircle, Check, FileSearch, Loader2, Package, Palette, Users } from "lucide-react";
import { useRotatingMessage } from "./useRotatingMessage";
import type { StepState, StepStatus } from "./types";

const colorByStatus: Record<StepStatus, string> = {
  idle: "text-muted-foreground/50",
  running: "text-indigo-600",
  done: "text-emerald-600",
  error: "text-destructive",
};

function Indicator({ status }: { status: StepStatus }) {
  switch (status) {
    case "running":
      return <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />;
    case "done":
      return <Check className="w-3.5 h-3.5 text-emerald-600" />;
    case "error":
      return <AlertCircle className="w-3.5 h-3.5 text-destructive" />;
    default:
      return <span className="block w-3.5 h-3.5 rounded-full border border-muted-foreground/30" />;
  }
}

function ProgressLine({
  icon,
  label,
  state,
  runningMessage,
}: {
  icon: React.ReactNode;
  label: string;
  state: StepState;
  runningMessage: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 shrink-0"><Indicator status={state.status} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={colorByStatus[state.status]}>{icon}</span>
          <span className="text-xs font-medium">{label}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
          {state.status === "running" ? runningMessage : state.errorMessage ?? state.message}
        </p>
      </div>
    </div>
  );
}

export function ProgressTimeline({
  steps,
}: {
  steps: { products: StepState; audiences: StepState; brandVoice: StepState };
}) {
  const productsRotating = useRotatingMessage(steps.products.status === "running", "products");
  const audiencesRotating = useRotatingMessage(steps.audiences.status === "running", "audiences");
  const brandVoiceRotating = useRotatingMessage(steps.brandVoice.status === "running", "brandVoice");

  return (
    <Card className="p-4 bg-muted/20 border-dashed">
      <div className="flex items-center gap-2 mb-3">
        <FileSearch className="w-4 h-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Análise por IA</h4>
      </div>
      <div className="space-y-2.5">
        <ProgressLine
          icon={<Package className="w-3.5 h-3.5" />}
          label="Produtos & ofertas"
          state={steps.products}
          runningMessage={productsRotating}
        />
        <ProgressLine
          icon={<Users className="w-3.5 h-3.5" />}
          label="Públicos-alvo"
          state={steps.audiences}
          runningMessage={audiencesRotating}
        />
        <ProgressLine
          icon={<Palette className="w-3.5 h-3.5" />}
          label="Voz da marca"
          state={steps.brandVoice}
          runningMessage={brandVoiceRotating}
        />
      </div>
    </Card>
  );
}
